import { Link } from "react-router-dom";
import { CommunityShell, C, COMMUNITY_MODULES } from "../../components/CommunityShell";

const STEPS = [
  { n: 1, title: "Ask about anything in your body", body: "Type a condition, a symptom, a lab value, or a medicine — in your own words. No medical jargon needed. \u201CWhy are my ankles swollen?\u201D works just as well as \u201Cheart failure.\u201D" },
  { n: 2, title: "Get the full, unhurried explanation", body: "We explain what's actually happening inside your body — the way a great doctor would if they had an hour instead of ten minutes. Plain English, real answers, nothing hidden behind jargon." },
  { n: 3, title: "See both sides of your care", body: "Your prescriptions, your lab numbers, upcoming scans and procedures — each one translated so you know what it does, why it matters, and what to ask at your next visit." },
  { n: 4, title: "Live it at home", body: "Cook dishes you love adapted for your condition, find real help near you (food, housing, coverage), and understand how home life shapes your health." },
];

const TUTOR_STEP = {
  n: 5,
  title: "Still have questions? Talk it through",
  body: "The AI Health Guide is a friendly back-and-forth conversation about anything health-related. Ask a follow-up, ask it to explain simpler, ask what to bring up with your doctor — it remembers the conversation and never rushes you. You'll also see an \u201CAsk\u201D button on condition, lab, and medicine pages, already loaded with what you were reading.",
};

export default function CommunityHowItWorks() {
  return (
    <CommunityShell
      testid="community-how-it-works-page"
      eyebrow="How it works"
      title="Understanding your own body, step by step."
      intro="The Community is the same clinical knowledge we teach nurses and nurse practitioners — rewritten for you, your family, and the people who care for you."
    >
      {/* Steps */}
      <section data-testid="community-hiw-steps" className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-16">
        {STEPS.map((s) => (
          <div
            key={s.n}
            data-testid={`community-hiw-step-${s.n}`}
            className="rounded-2xl border p-6"
            style={{ background: C.CARD, borderColor: C.BORDER }}
          >
            <span
              className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[13px] font-bold mb-3"
              style={{ background: C.PANEL, color: C.DEEP, border: `1px solid ${C.BORDER}` }}
            >
              {s.n}
            </span>
            <h3 className="text-[18px] font-bold tracking-tight mb-2" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{s.title}</h3>
            <p className="text-[14px] leading-relaxed" style={{ color: C.SUB }}>{s.body}</p>
          </div>
        ))}
        {/* Featured — AI Health Guide */}
        <div
          data-testid={`community-hiw-step-${TUTOR_STEP.n}`}
          className="sm:col-span-2 rounded-2xl border p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8"
          style={{ background: "linear-gradient(165deg, #F6EBD9 0%, #FAF5EC 100%)", borderColor: C.BORDER }}
        >
          <div className="flex-1">
            <span
              className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[13px] font-bold mb-3"
              style={{ background: C.ACCENT, color: "#FFFDF8" }}
            >
              {TUTOR_STEP.n}
            </span>
            <h3 className="text-[18px] font-bold tracking-tight mb-2" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{TUTOR_STEP.title}</h3>
            <p className="text-[14px] leading-relaxed max-w-2xl" style={{ color: C.SUB }}>{TUTOR_STEP.body}</p>
          </div>
          <Link
            to="/community/tutor"
            data-testid="community-hiw-tutor-cta"
            className="shrink-0 px-6 py-3 rounded-full text-[14px] font-bold transition-all hover:-translate-y-0.5"
            style={{ background: C.ACCENT, color: "#FFFDF8", fontFamily: C.SANS }}
          >
            Ask the Health Guide →
          </Link>
        </div>
      </section>

      {/* What's inside */}
      <section data-testid="community-hiw-modules" className="mb-16">
        <p className="text-[12px] font-bold uppercase tracking-[0.26em] mb-3" style={{ color: C.ACCENT }}>What's inside</p>
        <h2 className="text-[26px] sm:text-[32px] font-extrabold tracking-tight mb-6" style={{ color: C.INK, fontFamily: C.DISPLAY }}>
          Ten ways to explore.
        </h2>
        <div className="flex flex-wrap gap-2">
          {[...COMMUNITY_MODULES, { to: "/community/tutor", label: "AI Health Guide" }].map((m) => (
            <Link
              key={m.to} to={m.to}
              data-testid={`community-hiw-module-${m.to.replace(/[/]/g, "-")}`}
              className="px-4 py-2 rounded-full text-[13px] font-semibold transition-all hover:-translate-y-0.5"
              style={{ background: C.PANEL, color: C.DEEP, border: `1px solid ${C.BORDER}`, fontFamily: C.SANS }}
            >
              {m.label}
            </Link>
          ))}
        </div>
      </section>

      {/* Facilities & partners pointer */}
      <section
        data-testid="community-hiw-facilities-link"
        className="rounded-2xl border p-6 sm:p-8 mb-16 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8"
        style={{ background: C.CARD, borderColor: C.BORDER }}
      >
        <div className="flex-1">
          <p className="text-[12px] font-bold uppercase tracking-[0.26em] mb-2" style={{ color: C.ACCENT }}>For facilities & partners</p>
          <h2 className="text-[22px] sm:text-[26px] font-extrabold tracking-tight mb-2" style={{ color: C.INK, fontFamily: C.DISPLAY }}>
            Run a clinic, community center, or health plan?
          </h2>
          <p className="text-[14px] leading-relaxed max-w-2xl" style={{ color: C.SUB }}>
            See how doctors' offices, community centers, and insurance companies
            use The Community to help their patients and members.
          </p>
        </div>
        <Link
          to="/community/facilities"
          data-testid="community-hiw-facilities-cta"
          className="shrink-0 px-6 py-3 rounded-full text-[14px] font-bold transition-all hover:-translate-y-0.5"
          style={{ background: C.ACCENT, color: "#FFFDF8", fontFamily: C.SANS }}
        >
          Facilities & Partners →
        </Link>
      </section>

      {/* CTA */}
      <section
        data-testid="community-hiw-cta"
        className="rounded-2xl border p-8 sm:p-10 text-center"
        style={{ background: "linear-gradient(165deg, #F6EBD9 0%, #FAF5EC 100%)", borderColor: C.BORDER }}
      >
        <h2 className="text-[24px] sm:text-[30px] font-extrabold tracking-tight mb-3" style={{ color: C.INK, fontFamily: C.DISPLAY }}>
          Start with whatever's on your mind.
        </h2>
        <p className="text-[15px] mb-6 max-w-xl mx-auto" style={{ color: C.SUB }}>
          A diagnosis you just got, a pill in your cabinet, a number on a lab
          printout — pick one and we'll explain it.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/community"
            data-testid="community-hiw-cta-home"
            className="px-6 py-3 rounded-full text-[14px] font-bold transition-all hover:-translate-y-0.5"
            style={{ background: C.ACCENT, color: "#FFFDF8", fontFamily: C.SANS }}
          >
            Explore The Community →
          </Link>
          <Link
            to="/community/symptoms"
            data-testid="community-hiw-cta-symptoms"
            className="px-6 py-3 rounded-full text-[14px] font-bold transition-all hover:-translate-y-0.5"
            style={{ background: C.CARD, color: C.DEEP, border: `1px solid ${C.BORDER}`, fontFamily: C.SANS }}
          >
            Check a symptom
          </Link>
          <Link
            to="/community/tutor"
            data-testid="community-hiw-cta-tutor"
            className="px-6 py-3 rounded-full text-[14px] font-bold transition-all hover:-translate-y-0.5"
            style={{ background: C.CARD, color: C.DEEP, border: `1px solid ${C.BORDER}`, fontFamily: C.SANS }}
          >
            Ask the AI Health Guide
          </Link>
        </div>
      </section>
    </CommunityShell>
  );
}
